import xlsx, { IJsonSheet, IColumn, ISettings, IContent } from "json-as-xlsx";
import moment from "moment";
import {
  VotacaoVotosPorPerguntaItem,
  VotacaoVotoIndividualItem,
} from "features/assembleia/typings";
import { sanitizeSheetName } from "infra/helpers/sanitizeSheetName";
import { RelatorioVotosFields } from "core/features/relatorios/typings";

interface ExportVotosXLSXProps {
  data: VotacaoVotosPorPerguntaItem[];
  votacaoNome: string;
  selectedFields?: RelatorioVotosFields[];
}

const formatUnidadesXLSX = (voto: VotacaoVotoIndividualItem) => {
  return voto.unidades
    .map((u) => `${u.numero}${u.blocoQuadra ? ` (${u.blocoQuadra.nome})` : ""}`)
    .join("; ");
};

export function exportVotosToXLSX({
  data,
  votacaoNome,
  selectedFields = [],
}: ExportVotosXLSXProps) {
  const settings: ISettings = {
    fileName: `Relatório de Votos - ${sanitizeSheetName(
      votacaoNome
    )} - ${moment().format("DD_MM_YYYY")}`,
    extraLength: 3,
    writeOptions: {},
  };

  const sheets: IJsonSheet[] = [];
  const shouldShow = (field: RelatorioVotosFields) =>
    selectedFields.includes(field);

  if (
    shouldShow("perguntaTitulo") ||
    shouldShow("perguntaTotalVotos") ||
    shouldShow("perguntaTotalPeso")
  ) {
    const summaryContent: IContent[] = data.map((item) => {
      const row: IContent = {};
      if (shouldShow("perguntaTitulo"))
        row.pergunta = `${item.pergunta.ordem + 1}. ${item.pergunta.titulo}`;
      if (shouldShow("perguntaTotalVotos")) row.totalVotos = item.totalVotos;
      if (shouldShow("perguntaTotalPeso")) row.totalPeso = item.totalPeso;
      return row;
    });

    const summaryColumns: IColumn[] = [];
    if (shouldShow("perguntaTitulo"))
      summaryColumns.push({ label: "Pergunta", value: "pergunta" });
    if (shouldShow("perguntaTotalVotos"))
      summaryColumns.push({
        label: "Total de Votos na Pergunta",
        value: "totalVotos",
        format: "#",
      });
    if (shouldShow("perguntaTotalPeso"))
      summaryColumns.push({
        label: "Total de Peso na Pergunta",
        value: "totalPeso",
        format: "#,##0.00",
      });

    if (
      summaryColumns.length > 0 &&
      summaryContent.some((r) => Object.keys(r).length > 0)
    ) {
      sheets.push({
        sheet: "Resumo Geral Perguntas",
        columns: summaryColumns,
        content: summaryContent.filter((r) => Object.keys(r).length > 0), // Filter out empty rows
      });
    }
  }

  const individualVoteFieldsSelected = [
    "votanteNome",
    "votanteUnidades",
    "votantePesoTotal",
    "votanteDataVoto",
  ].some((f) => shouldShow(f as RelatorioVotosFields));

  if (individualVoteFieldsSelected) {
    let detailedContent: IContent[] = [];
    data.forEach((perguntaItem) => {
      perguntaItem.opcoes.forEach((opcaoItem) => {
        opcaoItem.votos.forEach((voto) => {
          const row: IContent = {};
          if (shouldShow("perguntaTitulo"))
            row.perguntaTitulo = `${perguntaItem.pergunta.ordem + 1}. ${
              perguntaItem.pergunta.titulo
            }`;
          if (shouldShow("opcaoTitulo"))
            row.opcaoTitulo = opcaoItem.opcao.titulo;
          if (shouldShow("votanteNome")) row.votanteNome = voto.pessoa.nome;
          if (shouldShow("votanteUnidades"))
            row.votanteUnidades = formatUnidadesXLSX(voto);
          if (shouldShow("votantePesoTotal"))
            row.votantePesoTotal = voto.pesoTotal;
          if (shouldShow("votanteDataVoto"))
            row.votanteDataVoto = moment(voto.dataVoto).format(
              "DD/MM/YYYY HH:mm:ss"
            );
          if (Object.keys(row).length > 0) detailedContent.push(row);
        });
      });
    });

    const detailedColumns: IColumn[] = [];
    if (shouldShow("perguntaTitulo"))
      detailedColumns.push({ label: "Pergunta", value: "perguntaTitulo" });
    if (shouldShow("opcaoTitulo"))
      detailedColumns.push({ label: "Opção Votada", value: "opcaoTitulo" });
    if (shouldShow("votanteNome"))
      detailedColumns.push({ label: "Nome do Votante", value: "votanteNome" });
    if (shouldShow("votanteUnidades"))
      detailedColumns.push({
        label: "Unidades do Votante",
        value: "votanteUnidades",
      });
    if (shouldShow("votantePesoTotal"))
      detailedColumns.push({
        label: "Peso Total do Voto",
        value: "votantePesoTotal",
        format: "#,##0.00",
      });
    if (shouldShow("votanteDataVoto"))
      detailedColumns.push({
        label: "Data/Hora do Voto",
        value: "votanteDataVoto",
      });

    if (detailedColumns.length > 0 && detailedContent.length > 0) {
      sheets.push({
        sheet: "Votos Individuais Detalhado",
        columns: detailedColumns,
        content: detailedContent,
      });
    }
  }

  if (
    !individualVoteFieldsSelected &&
    (shouldShow("opcaoTitulo") ||
      shouldShow("opcaoTotalVotos") ||
      shouldShow("opcaoTotalPeso"))
  ) {
    let optionSummaryContent: IContent[] = [];
    data.forEach((perguntaItem) => {
      perguntaItem.opcoes.forEach((opcaoItem) => {
        const row: IContent = {};
        if (shouldShow("perguntaTitulo"))
          row.perguntaTitulo = `${perguntaItem.pergunta.ordem + 1}. ${
            perguntaItem.pergunta.titulo
          }`;
        if (shouldShow("opcaoTitulo")) row.opcaoTitulo = opcaoItem.opcao.titulo;
        if (shouldShow("opcaoTotalVotos"))
          row.opcaoTotalVotos = opcaoItem.totalVotos;
        if (shouldShow("opcaoTotalPeso"))
          row.opcaoTotalPeso = opcaoItem.totalPeso;
        if (Object.keys(row).length > 0) optionSummaryContent.push(row);
      });
    });

    const optionSummaryColumns: IColumn[] = [];
    if (shouldShow("perguntaTitulo"))
      optionSummaryColumns.push({ label: "Pergunta", value: "perguntaTitulo" });
    if (shouldShow("opcaoTitulo"))
      optionSummaryColumns.push({ label: "Opção", value: "opcaoTitulo" });
    if (shouldShow("opcaoTotalVotos"))
      optionSummaryColumns.push({
        label: "Total de Votos na Opção",
        value: "opcaoTotalVotos",
        format: "#",
      });
    if (shouldShow("opcaoTotalPeso"))
      optionSummaryColumns.push({
        label: "Total de Peso na Opção",
        value: "opcaoTotalPeso",
        format: "#,##0.00",
      });

    if (optionSummaryColumns.length > 0 && optionSummaryContent.length > 0) {
      sheets.push({
        sheet: "Resumo por Opção",
        columns: optionSummaryColumns,
        content: optionSummaryContent,
      });
    }
  }

  if (sheets.length === 0) {
    sheets.push({
      sheet: "Sem dados para exibir",
      columns: [{ label: "Mensagem", value: "message" }],
      content: [
        {
          message:
            "Nenhum campo selecionado resultou em dados para esta exportação.",
        },
      ],
    });
  }

  xlsx(sheets, settings);
}
